'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'

import { ConsultarUsuario } from '@/src/components/Navegador/imagemUsuario'
import { getAuthToken } from '@/src/lib/cockies'
import { VerificarEmail, VerificarNome } from '@/src/lib/decode'
import { MeEmpresas, MeFuncionarios } from './MeusDados'
import { buscarContatos } from '../Contatos/api'
import { ListaContatosResponse, Contato } from '../Contatos/types'
import { logger } from '@/src/lib/logger'

/**
 * TODO: Substituir 'any' por uma interface tipada com os dados exatos do usuário futuramente.
 * Exemplo: createContext<IUsuarioContext | null>(null)
 */
export const UsuarioContext = createContext<any>(null)

const USER_CACHE_KEY = 'conecta:usuario_context'

/**
 * Recupera de forma segura os dados do usuário armazenados no SessionStorage.
 * O SessionStorage é ideal aqui pois os dados são limpos ao fechar o navegador,
 * aumentando a segurança.
 * * @returns O objeto do usuário parseado ou null caso não exista/falhe.
 */
const readCachedUser = () => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(USER_CACHE_KEY)
    if (!raw) return null

    return JSON.parse(raw)
  } catch (error) {
    logger.error('[UsuarioContext] Erro ao ler cache do usuário', error as Error)
    return null
  }
}

/**
 * Persiste os dados do usuário no SessionStorage para evitar consultas
 * redundantes ao backend durante a navegação do usuário.
 * * @param user - Objeto com os dados do usuário normalizados. Passe null para limpar o cache.
 */
const saveCachedUser = (user: any) => {
  if (typeof window === 'undefined') return

  try {
    if (!user) {
      window.sessionStorage.removeItem(USER_CACHE_KEY)
      return
    }

    window.sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(user))
  } catch (error) {
    // Falhas no storage não devem quebrar a aplicação (ex: navegação anônima agressiva)
    logger.warn('[UsuarioContext] Aviso: Não foi possível salvar o cache no storage', error as any)
  }
}

/**
 * Provider global responsável por gerenciar a sessão, os dados e os
 * privilégios do usuário logado em toda a aplicação.
 */
export function UseProvider({ children }: PropsWithChildren<{}>) {
  // Inicializa o estado com o cache (se existir) para evitar telas de carregamento desnecessárias (FOUC)
  const [usuario, setUsuario] = useState<any>(() => readCachedUser())

  // Lock de concorrência: Impede que a API seja chamada múltiplas vezes simultaneamente
  // caso múltiplos componentes disparem a função carregarUsuario() ao mesmo tempo.
  const carregandoRef = useRef<Promise<void> | null>(null)

  /**
   * Orquestra a busca, normalização e armazenamento dos dados do usuário.
   * * @param _state - Parâmetro reservado (não utilizado no fluxo principal).
   * @param options - Configurações extras (ex: { force: true } ignora o cache e o lock atual).
   * @returns Uma promise indicando o término da rotina de carregamento.
   */
  const carregarUsuario = async (_state?: unknown, options?: { force?: boolean }) => {
    // Retorna a promise em andamento se já estiver carregando (evita race conditions)
    if (carregandoRef.current && !options?.force) {
      return carregandoRef.current
    }

    const run = async () => {
      const token = getAuthToken()

      if (!token) {
        setUsuario(null)
        saveCachedUser(null)
        return
      }

      // Validação de integridade do cache: garante que o cache pertence ao token atual
      const cachedUser = readCachedUser()
      if (cachedUser?.token === token && !options?.force) {
        setUsuario(cachedUser)
        return
      }

      try {
        // 1. Decodificação do JWT localmente
        const email = await VerificarEmail(token)
        const nome = await VerificarNome(token)
        if (!email) throw new Error('Email não encontrado no token JWT')

        // 2. Consulta dos dados primários do usuário na API
        const user = await ConsultarUsuario(token)
        if (!user) throw new Error('Usuário não encontrado na base de dados')

        const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL
        const urlImagem = `${API_BASE_URL}/usuarios/${user.ID}/foto/`

        // 3. Consultas paralelas para dados relacionais (Empresas, Contatos, Funcionários)
        const empresas = await MeEmpresas(token)
        const contatosResponse = await buscarContatos(token)
        await MeFuncionarios(token)

        const contatos = (contatosResponse.contatos || []).filter(
          (c: Contato) => c.cargo === 'Key Account'
        )

        // Utilitário para extrair e remover duplicatas de propriedades dentro das empresas
        const normalizar = (key: string) => [
          ...new Set((empresas || []).map((e: any) => e[key]).filter(Boolean)),
        ]

        // 4. Montagem e normalização do payload final
        const usuarioNormalizado = {
          ...user,
          nome,
          email,
          token,
          urlImagem,
          CNPJS: normalizar('CNPJ'),
          CodigosCliente: normalizar('CodigoCliente'),
          GrupoEconomico: normalizar('GrupoEconomico'),
          Gerente:
            contatos.length > 0 ? { nome: contatos[0].nome, email: contatos[0].email } : null,
        }

        // 5. Atualização de estado e persistência no cache
        setUsuario(usuarioNormalizado)
        saveCachedUser(usuarioNormalizado)
      } catch (error) {
        logger.error(
          '[UsuarioContext] Falha crítica ao orquestrar carregamento do usuário',
          error as Error
        )
      }
    }

    // Gerenciamento do Lock: Atribui a execução atual à referência e limpa ao finalizar
    carregandoRef.current = run().finally(() => {
      carregandoRef.current = null
    })

    return carregandoRef.current
  }

  // Efeito de Inicialização: Disparado apenas uma vez quando o Provider é montado na raiz do app
  useEffect(() => {
    carregarUsuario()
  }, [])

  // Efeito de Sincronização: Mantém o cache e o sistema de logs atualizados a cada mudança no estado do usuário
  useEffect(() => {
    logger.debug('[UsuarioContext] Estado do usuário modificado', { userId: usuario?.ID })
    saveCachedUser(usuario)
  }, [usuario])

  return (
    <UsuarioContext.Provider
      value={{
        usuario,
        setUsuario,
        carregarUsuario,
      }}
    >
      {children}
    </UsuarioContext.Provider>
  )
}

/**
 * Hook customizado para acessar o contexto do usuário de forma simplificada em qualquer componente.
 * @example const { usuario, carregarUsuario } = useUsuario()
 */
export const useUsuario = () => useContext(UsuarioContext)
